import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SellIcon from "@mui/icons-material/Sell";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { Box, Typography, Button, Alert } from "@mui/material";
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { Action } from "../../types/toolbar";
import CustomModal from "../../components/ui/modal/modal.component";
import DynamicForm from "../../components/ui/dynamicForm/dynamicForm.component";
import PartForm from "../../components/service/partForm.component";
import OrderedPartForm from "../../components/service/orderedPartForm.component";
import { SendByCourierModal } from "../../components/shipping/sendByCourierModal.component";
import { useCRUD } from "../../api/crud";
import { PARTS_ENDPOINT, USED_PARTS_ENDPOINT, JOB_TASKS_ENDPOINT } from "../../consts";
import { SUCCESS_SNACKBAR, ERROR_SNACKBAR } from "../../consts";
import { useHasPermission } from "../../contexts/auth";
import { ApiFieldConfigMap } from "../../types/dynamicForm";

export const PartDetailsActions = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, error, remove } = useCRUD(PARTS_ENDPOINT);
  const { get: getPart } = useCRUD(PARTS_ENDPOINT);
  const [updatePartOpened, setUpdatePartOpened] = useState<boolean>(false);
  const [deletePartOpened, setDeletePartOpened] = useState<boolean>(false);
  const [addOrderedPartOpened, setAddOrderedPartOpened] = useState<boolean>(false);
  const [addUsedPartOpened, setAddUsedPartOpened] = useState<boolean>(false);
  const [editOrderedPartOpened, setEditOrderedPartOpened] = useState<boolean>(false);
  // The order the part's current price comes from - the one the next use is
  // drawn from. Editing it is the common correction (a delivery booked at
  // the wrong price), so it gets its own action rather than making the user
  // find the row in the Ordered Parts tab.
  const [currentOrderedPart, setCurrentOrderedPart] = useState<{
    id: number;
    number: string | null;
  } | null>(null);
  const [sendByCourierOpened, setSendByCourierOpened] = useState<boolean>(false);
  // What the shipment form starts from: this part, with its stock so the
  // quantity can be checked before anything is priced.
  const [partForShipment, setPartForShipment] = useState<{
    id: number;
    name: string;
    stock_quantity: string;
    unit: string;
  } | null>(null);

  const canChangePart = useHasPermission("service.change_part");
  const canDeletePart = useHasPermission("service.delete_part");
  const canAddOrderedPart = useHasPermission("service.add_orderedpart");
  const canAddUsedPart = useHasPermission("service.add_usedpart");
  const canChangeOrderedPart = useHasPermission("service.change_orderedpart");
  const canAddShipment = useHasPermission("shipping.add_shipment");

  const fetchCurrentOrderedPart = useCallback(async () => {
    if (!id || (!canChangeOrderedPart && !canAddShipment)) {
      setCurrentOrderedPart(null);
      return;
    }

    const response = await getPart(id);
    const part = response?.data;
    setPartForShipment(
      part ? { id: part.id, name: part.name, stock_quantity: part.stock_quantity, unit: part.unit } : null
    );
    if (!canChangeOrderedPart) {
      setCurrentOrderedPart(null);
      return;
    }
    const currentPrice = response?.data?.current_price;
    setCurrentOrderedPart(
      currentPrice
        ? { id: currentPrice.ordered_part_id, number: currentPrice.number }
        : null
    );
  }, [id, canChangeOrderedPart, canAddShipment, getPart]);

  useEffect(() => {
    fetchCurrentOrderedPart();

    // Which order is "current" moves when batches are added, edited or used up.
    const events = ["partUpdated", "orderedPartAdded", "orderedPartUpdated", "usedPartAdded", "usedPartUpdated"];
    events.forEach((event) => window.addEventListener(event, fetchCurrentOrderedPart));
    return () => {
      events.forEach((event) => window.removeEventListener(event, fetchCurrentOrderedPart));
    };
  }, [fetchCurrentOrderedPart]);

  const handleDeletePart = async () => {
    if (loading || !id) {
      return;
    }

    const result = await remove({ id });

    if (result?.success) {
      enqueueSnackbar(t('Part deleted successfully'), SUCCESS_SNACKBAR);
      setDeletePartOpened(false);
      navigate('/service/stock-status');
    } else {
      enqueueSnackbar(t('Failed to delete part'), ERROR_SNACKBAR);
    }
  };

  const handleOrderedPartAdded = () => {
    setAddOrderedPartOpened(false);
    window.dispatchEvent(new Event("orderedPartAdded"));
    window.dispatchEvent(new Event("partUpdated"));
  };

  const handleUsedPartAdded = () => {
    setAddUsedPartOpened(false);
    window.dispatchEvent(new Event("usedPartAdded"));
    window.dispatchEvent(new Event("partUpdated"));
  };

  const handleOrderedPartUpdated = () => {
    setEditOrderedPartOpened(false);
    window.dispatchEvent(new Event("orderedPartUpdated"));
    window.dispatchEvent(new Event("partUpdated"));
  };

  const actions: Action[] = [
    ...(canAddOrderedPart ? [{
      id: "add-ordered-part",
      icon: <AddIcon />,
      label: t("Add ordered part"),
      onClick: () => {
        setAddOrderedPartOpened(true);
      },
      disabled: false
    }] : []),
    ...(canAddUsedPart ? [{
      id: "add-used-part",
      icon: <AddIcon />,
      label: t("Add used part"),
      onClick: () => {
        setAddUsedPartOpened(true);
      },
      disabled: false
    }] : []),
    ...(canAddShipment ? [{
      id: "send-by-courier",
      icon: <LocalShippingIcon />,
      label: t("Send by courier"),
      onClick: () => {
        setSendByCourierOpened(true);
      },
      disabled: !partForShipment || Number(partForShipment.stock_quantity) <= 0,
    }] : []),
    ...(currentOrderedPart ? [{
      id: "edit-current-ordered-part",
      icon: <SellIcon />,
      label: t("Edit current order"),
      onClick: () => {
        setEditOrderedPartOpened(true);
      },
      disabled: false
    }] : []),
    {
      icon: <EditIcon />,
      label: t("Update part"),
      onClick: () => {
        setUpdatePartOpened(true);
      },
      disabled: !canChangePart,
    },
    {
      icon: <DeleteIcon />,
      label: t("Delete part"),
      onClick: () => {
        setDeletePartOpened(true);
      },
      color: "var(--errorColor)",
      disabled: !canDeletePart,
    },
  ];

  const usedPartApiFields: ApiFieldConfigMap = {
    part: {
      url: PARTS_ENDPOINT,
      field: "name",
      queryParams: { in_stock: "true", is_active: "true" },
    },
    job_task: { url: JOB_TASKS_ENDPOINT, field: "display_name" },
  };

  const errorMessage =
    typeof error === 'string'
      ? error
      : error?.message ?? error?.detail ?? '';

  const deleteModalContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('Deleting this part will permanently remove it from the system. This action cannot be undone.')}
      </Typography>

      <Alert severity="warning">
        <Typography variant="body2">
          {t('This action cannot be undone. All associated data will be permanently deleted.')}
        </Typography>
      </Alert>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Button
        variant="contained"
        color="error"
        onClick={handleDeletePart}
        disabled={loading}
      >
        {loading ? t('Processing...') : t('Confirm deletion')}
      </Button>
    </Box>
  );

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={updatePartOpened}
        setOpened={setUpdatePartOpened}
        title={t("Update part")}
        content={
          <PartForm
            id={id}
            onSuccess={() => {
              setUpdatePartOpened(false);
              window.dispatchEvent(new Event("partUpdated"));
            }}
          />
        }
      />
      <CustomModal
        opened={deletePartOpened}
        setOpened={setDeletePartOpened}
        title={t("Delete part")}
        content={deleteModalContent}
      />
      <CustomModal
        opened={addOrderedPartOpened}
        setOpened={setAddOrderedPartOpened}
        title={t("Add ordered part")}
        content={
          <OrderedPartForm
            initialPartId={id}
            onSuccess={handleOrderedPartAdded}
          />
        }
      />
      {/* Only mounted alongside its action, so the form always has an order
          to load. */}
      {currentOrderedPart && (
        <CustomModal
          opened={editOrderedPartOpened}
          setOpened={setEditOrderedPartOpened}
          title={t("Edit current order")}
          content={
            <OrderedPartForm
              id={String(currentOrderedPart.id)}
              initialPartId={id}
              onSuccess={handleOrderedPartUpdated}
            />
          }
        />
      )}
      <SendByCourierModal
        opened={sendByCourierOpened}
        setOpened={setSendByCourierOpened}
        initialPart={partForShipment}
      />
      <CustomModal
        opened={addUsedPartOpened}
        setOpened={setAddUsedPartOpened}
        title={t("Add used part")}
        content={
          <DynamicForm
            url={USED_PARTS_ENDPOINT}
            onSuccess={handleUsedPartAdded}
            apiFields={usedPartApiFields}
          />
        }
      />
    </>
  );
};
